import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../../modules/hooks';
import { selectAssetIdsMap } from '../../modules/assets';
import {
  selectIsWatchtimeFetched,
  selectIsWatchtimeFetching,
  selectIsSeriesWatchtimeFetched,
  selectIsSeriesWatchtimeFetching,
} from '../../modules/fetching';
import {
  selectAssetWatchtimeItem,
  selectSeriesWatchtime,
} from '../../modules/watchtime';
import {
  fetchSeriesWatchtimes,
  fetchWatchtimeHistory,
  fetchWatchtimes,
} from '../../modules/watchtime/actions';
import { config } from '../../config';
import { unleash } from '../../services/unleash';
import { selectSeriesIdsMap } from '../../modules/categories';

import type { RootState } from '../../modules/store';
import type { Vendor } from '../../types';
import type { SvpCategoryId } from '@vgtv/api-client/lib/svp_category';
import type { SvpAssetId } from '@vgtv/api-client/lib/svp_asset';

const selectAssetIdsToFetch = (
  state: RootState,
  assetIds: SvpAssetId[],
  vendor: Vendor
) => {
  return assetIds.filter((assetId) => {
    const isFetched = selectIsWatchtimeFetched(state, assetId, vendor);
    const isFetching = selectIsWatchtimeFetching(state, assetId, vendor);
    const watchtime = selectAssetWatchtimeItem(state, assetId, vendor);

    return !(isFetched || isFetching || watchtime);
  });
};

const selectSeriesIdsToFetch = (
  state: RootState,
  seriesIds: SvpCategoryId[],
  vendor: Vendor
) => {
  return seriesIds.filter((seriesId) => {
    const isFetched = selectIsSeriesWatchtimeFetched(state, seriesId, vendor);
    const isFetching = selectIsSeriesWatchtimeFetching(state, seriesId, vendor);
    const watchtime = selectSeriesWatchtime(state, vendor, seriesId);

    return !(isFetched || isFetching || watchtime);
  });
};

export const useWatchtime = () => {
  const dispatch = useAppDispatch();
  const storedAssetIdsMap = useAppSelector(selectAssetIdsMap);
  const storedSeriesIdsMap = useAppSelector(selectSeriesIdsMap);

  const assets = useAppSelector((state) =>
    storedAssetIdsMap.map(({ vendor, assetIds: storedAssetIds }) => {
      return {
        vendor,
        assetIds: selectAssetIdsToFetch(state, storedAssetIds, vendor),
      };
    })
  );
  const series = useAppSelector((state) =>
    storedSeriesIdsMap.map(({ vendor, seriesIds: storedSeriesIds }) => {
      return {
        vendor,
        seriesIds: selectSeriesIdsToFetch(state, storedSeriesIds, vendor),
      };
    })
  );

  useEffect(() => {
    if (!unleash.isEnabled('watchtime')) {
      return;
    }

    assets.forEach(({ vendor, assetIds }) => {
      if (!assetIds.length) {
        return;
      }

      dispatch(
        fetchWatchtimes({
          vendor,
          assetIds,
        })
      );
    });
  }, [dispatch, assets]);

  useEffect(() => {
    if (!unleash.isEnabled('watchtime')) {
      return;
    }

    series.forEach(({ vendor, seriesIds }) => {
      if (!seriesIds.length) {
        return;
      }

      dispatch(
        fetchSeriesWatchtimes({
          vendor,
          seriesIds,
        })
      );
    });
  }, [dispatch, series]);

  useEffect(() => {
    if (unleash.isEnabled('watchtime')) {
      dispatch(fetchWatchtimeHistory({ vendor: config.provider }));
    }
  }, [dispatch]);
};
